/**
 * Single source of truth for every verified fact about the business.
 *
 * Section copy, the contact UI, and the JSON-LD in `index.html` all read from
 * here. Nothing in this file may be guessed: each value traces to
 * `project-brief.md`. Anything the research could not confirm carries
 * `unverified: true` so it renders as a labelled placeholder and lands in the
 * outreach issue instead of shipping as fact.
 */

export const siteConfig = {
  appName: 'Black Bear Security and Medical',
  packageName: 'black-bear-security-and-medical',
  description:
    'Guarding, armed response, CCTV and alarm installation, and first aid, fire and security officer training. Based in Jet Park, working across the East Rand.',
  url: 'https://black-bear-security-and-medical.pages.dev',
} as const;

export const business = {
  legalName: 'Black Bear Security and Medical',
  shortName: 'Black Bear',
  /** Verified: project-brief.md — Google Maps listing. */
  telephoneDisplay: '079 291 8628',
  telephoneHref: 'tel:+27792918628',
  address: {
    unit: '1st Floor',
    street: '32 Kelly Rd',
    suburb: 'Jet Park',
    city: 'Boksburg',
    region: 'Gauteng',
    postalCode: '1620',
    country: 'ZA',
  },
  /** Verified: extracted from the Google Maps place URL in project-brief.md. */
  geo: { latitude: -26.1637029, longitude: 28.2242182 },
  mapsListingUrl:
    'https://www.google.com/maps/place/Black+Bear+Security+and+Medical/data=!4m7!3m6!1s0x1e9515d9692ad6fd:0xb2b5ac6652467dde!8m2!3d-26.1637029!4d28.2242182!16s%2Fg%2F11v4n3689b!19sChIJ_dYqadkVlR4R3n1GUmastbI',
  directionsUrl: 'https://www.google.com/maps/dir/?api=1&destination=-26.1637029,28.2242182',
  /** Verified: 4.3 across 31 Google reviews. Never printed without the listing link. */
  rating: { value: 4.3, count: 31, sourceLabel: 'Google' },
  socials: [
    { label: 'Facebook', url: 'https://www.facebook.com/p/Black-Bear-Security-and-Medical-61553908089620/' },
    { label: 'Instagram', url: 'https://www.instagram.com/black_bear_security_/' },
  ],
  /**
   * Opening hours were not published on any profile the research could reach.
   * Rendered as a labelled placeholder and omitted from the JSON-LD entirely —
   * an invented `openingHoursSpecification` is worse than a missing one.
   */
  hours: { unverified: true as const, label: 'Office hours to be confirmed by the owner' },
} as const;

export const serviceAreas = [
  'Jet Park',
  'Boksburg',
  'Kempton Park',
  'Ekurhuleni',
  'East Rand',
  'Johannesburg East',
] as const;

/**
 * Six service lines, taken from the Instagram bio, the Facebook page and the
 * Procompare listing. `plan` selects which drawn perimeter tile renders beside
 * the row — the site's repeating signature device.
 *
 * One sentence a row. The rows used to carry a lede and a detail line that
 * restated one another, which made this the longest section on the page.
 */
export const services = [
  {
    index: '01',
    slug: 'guarding',
    title: 'Guarding',
    summary: 'Officers posted to your gate, your yard and your night shift.',
    plan: 'gate',
  },
  {
    index: '02',
    slug: 'armed-response',
    title: 'Armed response',
    summary: 'Alarm monitoring, and a vehicle sent when your panel fires.',
    plan: 'response',
  },
  {
    index: '03',
    slug: 'cctv',
    title: 'CCTV and alarms',
    summary: 'Cameras, alarms, access control and gate automation, installed.',
    plan: 'camera',
  },
  {
    index: '04',
    slug: 'first-aid',
    title: 'First aid training',
    summary: 'Courses for the staff who get there before the ambulance.',
    plan: 'medical',
  },
  {
    index: '05',
    slug: 'fire',
    title: 'Basic fire fighting',
    summary: 'Fire classes and the right extinguisher for each one.',
    plan: 'fire',
  },
  {
    index: '06',
    slug: 'officer-training',
    title: 'Security officer training',
    summary: 'For new entrants and for teams already on the ground.',
    plan: 'training',
  },
] as const;

/**
 * The response sequence, scrubbed in the pinned chapter. Times are the reader's
 * clock through the stage list, not a published service-level promise — the copy
 * says so, and no response time is claimed anywhere on the page.
 */
export const responseStages = [
  { code: '01', label: 'Signal', note: 'The panel fires and the control room has it.' },
  { code: '02', label: 'Verify', note: 'Camera, call, keyholder. A real event or a cat.' },
  { code: '03', label: 'Dispatch', note: 'The nearest vehicle takes the address.' },
  { code: '04', label: 'Approach', note: 'Perimeter first, entry second.' },
  { code: '05', label: 'Secure', note: 'The site is walked and made safe.' },
  { code: '06', label: 'Report', note: 'You hear what happened and what was done.' },
] as const;
